/**
 * ART (Anotação de Responsabilidade Técnica) do profissional.
 *
 * Espelha o que a API devolve em `/professionals/:id/arts` — o model
 * `ProfessionalArt` do backend. Os campos internos do arquivo (`fileName`,
 * `filePath`, `fileSize`, `mimeType`, `uploadedBy`) não entram aqui: a tela
 * só precisa saber SE há anexo, e isso é `originalName`.
 */
export interface ProfessionalArt {
  id: number;
  professionalId: number;
  companyId: number;
  companyName?: string;
  artNumber: string;
  issueDate: string;
  expiryDate: string | null;
  originalName: string | null;
  notes: string | null;
}

/** Corpo do POST. `companyId` é obrigatório: sem ele o backend devolve 400. */
export interface ProfessionalArtInput {
  companyId: number;
  artNumber: string;
  issueDate: string;
  expiryDate: string | null;
  notes: string | null;
}

/**
 * Corpo do PUT. `companyId` e `professionalId` ficam de fora de propósito:
 * o backend rejeita a troca de qualquer um dos dois com ValidationError.
 */
export type ProfessionalArtUpdate = Omit<ProfessionalArtInput, 'companyId'>;

export type ArtStatus = 'valida' | 'vencida' | 'sem_validade';

/**
 * Data LOCAL em `YYYY-MM-DD`.
 *
 * `toISOString()` devolve a data em UTC: em fuso −03, das 21h à meia-noite ela
 * já é a de amanhã, e uma ART que vence hoje aparecia vencida antes da hora.
 */
const dataLocal = (d: Date): string => {
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
};

/**
 * Derivado de expiryDate na renderização — não existe coluna no banco.
 *
 * A comparação é TEXTUAL entre duas strings `YYYY-MM-DD`, que ordenam
 * lexicograficamente igual à ordem cronológica. `expiryDate` é DATEONLY e chega
 * como string: convertê-la para `Date` só reintroduziria erro de fuso.
 */
export const artStatus = (art: ProfessionalArt, hoje = new Date()): ArtStatus => {
  if (art.expiryDate === null) return 'sem_validade';
  return art.expiryDate >= dataLocal(hoje) ? 'valida' : 'vencida';
};
